import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { InvestmentConfirmation } from 'api-client';
import PrimaryButton, {
  PrimaryButtonColor,
} from 'components/common/buttons/decorators/PrimaryButton/PrimaryButton';
import { DocumentCard } from 'components/common/documents/DocumentCard';
import { ChevronIcon } from 'components/common/icons/ChevronIcon';
import { CrossIcon } from 'components/common/icons/CrossIcon';
import { ConfirmSmsCodeForm } from 'components/common/utils';
import { Loader } from 'components/common/utils/Loader';
import apiClientConfig from 'configs/app';
import smsConfig from 'configs/sms';
import {
  createInvestmentManager,
  createProfileManager,
  createRouteUrlProvider,
  createSmsFormStateStorage,
} from 'di';
import { formatDate } from 'formatters';
import { useAppDispatch } from 'hooks';
import { set } from 'store/overlay';
import { set as setProfile } from 'store/profile';
import { RouteDictionary } from 'utils';

import Modal from '../Modal';
import { ModalProps } from '../Modal/Modal';
import styles from './InvestmentConfirmationModal.module.scss';

type Step = 'document' | 'loading' | 'sms';

const SMS_REQUEST_COOLDOWN_MS = 60_000;

const getSmsCooldownStorageKey = (confirmationUuid: string): string =>
  `invest-conf-sms-requested-at-${confirmationUuid}`;

const readLastSmsRequestedAt = (confirmationUuid: string): number | null => {
  const raw = sessionStorage.getItem(getSmsCooldownStorageKey(confirmationUuid));
  if (!raw) {
    return null;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

const formatCooldown = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

const setSmsResendCooldown = (confirmationUuid: string): void => {
  const storage = createSmsFormStateStorage(`invest-conf-${confirmationUuid}`);
  const nextTryDate = new Date(Date.now() + SMS_REQUEST_COOLDOWN_MS);
  storage.set({
    attemptsLeft: smsConfig.smsMaxAttempts,
    nextTryDate,
  });
};

const resolveDocumentUrl = (uri: string): string =>
  uri.startsWith('http') ? uri : `${apiClientConfig.apiBaseUrl}${uri}`;

const getErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};

type Props = ModalProps & {
  confirmationUuid: string;
};

const InvestmentConfirmationModal: FC<Props> = ({ active, onClose, confirmationUuid }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [step, setStep] = useState<Step>('document');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stepError, setStepError] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<InvestmentConfirmation | null>(null);
  const [lastSmsRequestedAt, setLastSmsRequestedAt] = useState<number | null>(() =>
    readLastSmsRequestedAt(confirmationUuid)
  );
  const [now, setNow] = useState(() => Date.now());
  const isSuccessRedirectRef = useRef(false);

  const smsCooldownRemainingSec = useMemo(() => {
    if (!lastSmsRequestedAt) {
      return 0;
    }
    return Math.max(0, Math.ceil((lastSmsRequestedAt + SMS_REQUEST_COOLDOWN_MS - now) / 1000));
  }, [lastSmsRequestedAt, now]);

  const canRequestNewSms = smsCooldownRemainingSec === 0;
  const smsSent = lastSmsRequestedAt !== null;

  useEffect(() => {
    if (canRequestNewSms) {
      return;
    }
    const intervalId = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [canRequestNewSms]);

  const markSmsRequested = () => {
    const requestedAt = Date.now();
    setLastSmsRequestedAt(requestedAt);
    sessionStorage.setItem(getSmsCooldownStorageKey(confirmationUuid), String(requestedAt));
  };

  const clearSmsRequestCooldown = () => {
    setLastSmsRequestedAt(null);
    sessionStorage.removeItem(getSmsCooldownStorageKey(confirmationUuid));
  };

  const documentUrl = useMemo(() => {
    if (!confirmation?.documentUrl) {
      return '';
    }
    return resolveDocumentUrl(confirmation.documentUrl);
  }, [confirmation?.documentUrl]);

  const documentDate = useMemo(() => formatDate(new Date()), []);

  const isSmsStep = step === 'sms';
  const isRequestingSms = step === 'loading';
  const isDocumentPhase = !isSmsStep;
  const showDocumentLoader = loading || isRequestingSms;

  const showBack = isSmsStep;
  const showClose = !isRequestingSms;

  const handleModalClose = () => {
    if (isSuccessRedirectRef.current) {
      return;
    }
    onClose();
  };

  const goBack = () => {
    setStepError(null);
    if (isSmsStep) {
      setStep('document');
      return;
    }
    handleModalClose();
  };

  useEffect(() => {
    if (!active) {
      isSuccessRedirectRef.current = false;
      setStep('document');
      setLoading(true);
      setLoadError(null);
      setStepError(null);
      setConfirmation(null);
      return;
    }

    const loadConfirmation = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const manager = await createInvestmentManager();
        const result = await manager.getConfirmation(confirmationUuid);
        setConfirmation(result);
      } catch (error) {
        setConfirmation(null);
        setLoadError(getErrorMessage(error, 'Не удалось загрузить документ'));
      } finally {
        setLoading(false);
      }
    };

    void loadConfirmation();
  }, [active, confirmationUuid]);

  const requestSmsCode = async () => {
    setStepError(null);

    if (smsSent && !canRequestNewSms) {
      setStep('sms');
      return;
    }

    if (!canRequestNewSms) {
      return;
    }

    setStep('loading');

    try {
      const manager = await createInvestmentManager();
      await manager.sendCode(confirmationUuid);
      markSmsRequested();
      setSmsResendCooldown(confirmationUuid);
      setStep('sms');
    } catch (error) {
      setStepError(getErrorMessage(error, 'Не удалось отправить код подтверждения'));
      setStep('document');
    }
  };

  const handleSuccess = async () => {
    const manager = await createProfileManager();
    const profile = await manager.get();
    dispatch(setProfile(profile));
    dispatch(set(false));
    clearSmsRequestCooldown();
    isSuccessRedirectRef.current = true;
    const routeProvider = createRouteUrlProvider();
    navigate(routeProvider.getByName(RouteDictionary.MY_PROJECTS), { replace: true });
  };

  return (
    <Modal active={active} onClose={handleModalClose} className={styles.modal}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {showBack ? (
            <button type="button" className={styles.backButton} onClick={goBack} aria-label="Назад">
              <ChevronIcon direction="left" color="var(--white-color)" />
            </button>
          ) : (
            <div className={styles.headerLeftSpacer} />
          )}
        </div>
        <div className={styles.headerTitle}>Агентское поручение</div>
        <div className={styles.headerRight}>
          {showClose ? (
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleModalClose}
              aria-label="Закрыть"
            >
              <CrossIcon preset="default" />
            </button>
          ) : (
            <div className={styles.headerRightSpacer} />
          )}
        </div>
      </div>

      <div className={styles.body}>
        <div className={styles.stepShell}>
          <div className={styles.stepMain}>
            {isDocumentPhase ? (
              <div className={styles.confirmStep}>
                <p className={styles.confirmDescription}>
                  Ознакомьтесь и подтвердите агентское поручение на заключение сделки
                </p>
                <div className={styles.documentArea}>
                  {showDocumentLoader ? (
                    <Loader size="small" overlay={false} />
                  ) : loadError ? (
                    <div className={styles.hintError}>{loadError}</div>
                  ) : documentUrl ? (
                    <DocumentCard
                      className={styles.documentAreaCard}
                      title="Агентское поручение"
                      documentUrl={documentUrl}
                      date={documentDate}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <>
                {stepError ? <div className={styles.hintError}>{stepError}</div> : null}
                <ConfirmSmsCodeForm
                  variant="comfort"
                  compactLoading
                  title="Введите код"
                  prefix={`invest-conf-${confirmationUuid}`}
                  onCancel={() => {
                    setStepError(null);
                    setStep('document');
                  }}
                  sendCallback={async (smsCode) => {
                    const manager = await createInvestmentManager();
                    return manager.createInvestment(confirmationUuid, smsCode);
                  }}
                  resendCallback={async () => {
                    if (!canRequestNewSms) {
                      throw new Error(
                        `Новый код можно отправить через ${formatCooldown(smsCooldownRemainingSec)}`
                      );
                    }
                    const manager = await createInvestmentManager();
                    await manager.sendCode(confirmationUuid);
                    markSmsRequested();
                    setSmsResendCooldown(confirmationUuid);
                  }}
                  successCallback={handleSuccess}
                  onError={async (error) => {
                    setStepError(error.message);
                  }}
                />
              </>
            )}
          </div>

          {isDocumentPhase ? (
            <div className={styles.stepActions}>
              {stepError ? <div className={styles.footerError}>{stepError}</div> : null}
              {!canRequestNewSms && smsSent ? (
                <p className={styles.mutedLabel}>
                  Новый код можно отправить через {formatCooldown(smsCooldownRemainingSec)}
                </p>
              ) : null}
              <PrimaryButton expanded large color={PrimaryButtonColor.GREEN}>
                <button
                  type="button"
                  disabled={
                    showDocumentLoader ||
                    !documentUrl ||
                    Boolean(loadError) ||
                    (!canRequestNewSms && !smsSent)
                  }
                  onClick={() => void requestSmsCode()}
                >
                  {smsSent && !canRequestNewSms ? 'Продолжить ввод кода' : 'Подтвердить'}
                </button>
              </PrimaryButton>
            </div>
          ) : null}
        </div>
      </div>
    </Modal>
  );
};

export default InvestmentConfirmationModal;
